import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, Clock, Send, DollarSign } from 'lucide-react';
import { ContactMap } from '@/components/contact/ContactMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-zinc-50">
      {/* Hero Section — compact, lighter */}
      <section className="bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-red-500 text-[11px] font-bold uppercase tracking-[0.28em]">Get in Touch</span>
          <h1 className="text-4xl sm:text-5xl font-black text-white mt-3 mb-4 tracking-tight">
            Talk to a Logistics Expert
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto text-base">
            Need a quote, support, or partnership? Our team responds within 1 business hour, Monday – Friday.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Contact Info — slimmed */}
            <div className="lg:col-span-1 space-y-5">
              {/* Toll-Free */}
              <div className="bg-white border border-zinc-200 p-5 flex items-start gap-4 hover:border-red-300 transition-colors">
                <div className="w-10 h-10 bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1">Toll-Free</h3>
                  <a href="tel:+18776809272" className="inline-block py-1.5 font-bold text-zinc-900 hover:text-red-600 transition-colors">
                    1 (877) 680-9272
                  </a>
                </div>
              </div>

              {/* General Support */}
              <div className="bg-white border border-zinc-200 p-5 flex items-start gap-4 hover:border-red-300 transition-colors">
                <div className="w-10 h-10 bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1">General Inquiries</h3>
                  <a href="mailto:support@redballxpress.us" className="inline-block py-1.5 font-bold text-zinc-900 hover:text-red-600 transition-colors break-all">
                    support@redballxpress.us
                  </a>
                </div>
              </div>

              {/* Billing */}
              <div className="bg-white border border-zinc-200 p-5 flex items-start gap-4 hover:border-red-300 transition-colors">
                <div className="w-10 h-10 bg-red-50 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1">Billing (AP & AR)</h3>
                  <a href="tel:+19893915380" className="inline-block py-1.5 font-bold text-zinc-900 hover:text-red-600 transition-colors">
                    (989) 391-5380
                  </a>
                  <a href="mailto:billing@redballxpress.us" className="block py-1.5 text-sm text-zinc-500 hover:text-red-600 transition-colors break-all">
                    billing@redballxpress.us
                  </a>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white border border-zinc-200 p-5 flex items-start gap-4 hover:border-red-300 transition-colors">
                <div className="w-10 h-10 bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1">Business Hours</h3>
                  <p className="text-zinc-900 font-medium">Mon – Fri · 8 AM – 6 PM</p>
                  <p className="text-xs text-red-600 font-bold mt-0.5">24/7 Emergency Support</p>
                </div>
              </div>

              {/* Key contacts — collapsed into a single elegant block */}
              <div className="bg-zinc-900 text-white p-5 space-y-4">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-red-400">Direct Lines</h3>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5">Emergency / Operations</div>
                  <a href="mailto:gary.a@redballxpress.us" className="block py-1.5 text-sm font-medium text-white hover:text-red-400 transition-colors break-all">gary.a@redballxpress.us</a>
                  <a href="tel:+19893915075" className="block py-1.5 text-xs text-zinc-400 hover:text-red-400 transition-colors">(989) 391-5075</a>
                </div>
                <div className="pt-3 border-t border-zinc-800">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5">Human Resources</div>
                  <a href="mailto:hr@redballxpress.us" className="block py-1.5 text-sm font-medium text-white hover:text-red-400 transition-colors break-all">hr@redballxpress.us</a>
                </div>
                <div className="pt-3 border-t border-zinc-800">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5">CEO Office</div>
                  <a href="mailto:rammy.k@redballxpress.us" className="block py-1.5 text-sm font-medium text-white hover:text-red-400 transition-colors break-all">rammy.k@redballxpress.us</a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 lg:p-10 border border-zinc-200">
                <div className="mb-7">
                  <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 mb-2 tracking-tight">Send Us a Message</h2>
                  <p className="text-sm text-zinc-500">Average response time: under 1 hour during business hours.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:border-zinc-900 outline-none transition-all"
                        placeholder="John Doe"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:border-zinc-900 outline-none transition-all"
                        placeholder="john@example.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:border-zinc-900 outline-none transition-all"
                        placeholder="(555) 123-4567"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:border-zinc-900 outline-none transition-all"
                        placeholder="How can we help?"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:border-zinc-900 outline-none transition-all resize-none"
                      placeholder="Tell us about your logistics needs..."
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold uppercase tracking-wide transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 w-full md:w-auto"
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Office Locations Map */}
      <ContactMap />
    </div>
  );
};

export default ContactPage;
